/* ==========================================================================
   CUSTOM COLOUR-GROUPED PLP
   --------------------------------------------------------------------------
   Responsibilities:
     1. Pull the collection's remaining pages and merge each page's cards into
        the colour groups already rendered on the page.
     2. Scroll to a colour group when its circle is tapped.
     3. Highlight the circle of whichever group is in view (scroll-spy).
     4. Hide colour groups that turn out to have no products.

   The section renders the same skeleton for every page, so a fetched page
   arrives with its products already sorted into groups. Merging is therefore
   just "move this group's cards into the matching group in the DOM", which
   keeps the client dumb and the grouping authoritative on the server.
   ========================================================================== */

   (function () {
    'use strict';
  
    if (customElements.get('custom-color-plp')) return;
  
    var OTHER_KEY = '__ccp_other';
    var PENDING_TIMEOUT = 5000;
    var SPY_SUPPRESS_MS = 900;
  
    class CustomColorPlp extends HTMLElement {
      connectedCallback() {
        if (this._initialised) return;
        this._initialised = true;
  
        this.navWrap = this.querySelector('[data-ccp-nav-wrap]');
        this.navList = this.querySelector('[data-ccp-nav]');
        this.groups = Array.from(this.querySelectorAll('[data-ccp-group]'));
        this.links = Array.from(this.querySelectorAll('[data-ccp-link]'));
  
        this.spyEnabled = this.getAttribute('data-ccp-spy') === 'true';
        this.totalPages = parseInt(this.getAttribute('data-ccp-pages'), 10) || 1;
        this.currentPage = parseInt(this.getAttribute('data-ccp-page'), 10) || 1;
  
        this.loaded = false;
        this.engaged = false;
        this.activeKey = undefined;
        this.pendingKey = null;
        this.pendingTimer = null;
        this.suppressSpyUntil = 0;
        this.rafId = null;
  
        this.measureNav();
        this.watchNavSize();
        this.bindNav();
        this.bindScroll();
        this.updateStuck();
        this.captureIncomingHash();
  
        if (this.currentPage === 1 && this.totalPages > 1) {
          this.loadRemainingPages();
        } else {
          this.finish();
        }
      }
  
      disconnectedCallback() {
        if (this.onScrollBound) window.removeEventListener('scroll', this.onScrollBound);
        if (this.onResizeBound) window.removeEventListener('resize', this.onResizeBound);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.pendingTimer) clearTimeout(this.pendingTimer);
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }
  
      /* ---------------------------------------------------------------- *
       * Lookups
       * ---------------------------------------------------------------- */
  
      groupFor(key) {
        return this.groups.find((group) => group.getAttribute('data-ccp-group') === key) || null;
      }

      productKey(card) {
        var productLink = card.querySelector('.product-item-meta__title[href], .product-item__aspect-ratio[href]');
        var href = productLink && productLink.getAttribute('href');
        if (!href) return null;

        /* Cards from a fetched page live in a DOMParser document, so `.href`
           resolves to a different base URL than a card already on the page.
           Compare the canonical product path instead. */
        try {
          return new URL(href, window.location.origin).pathname;
        } catch (error) {
          return href;
        }
      }
  
      linkFor(key) {
        return this.links.find((link) => link.getAttribute('data-ccp-link') === key) || null;
      }
  
      prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
  
      /* ---------------------------------------------------------------- *
       * Geometry
       * ---------------------------------------------------------------- */
  
      /* The CSS owns the header / announcement-bar maths. Reading the resolved
         sticky `top` back means there is never a magic number to keep in sync,
         and the offset stays correct if the header configuration changes. */
      getScrollOffset() {
        if (!this.navWrap) return 0;
  
        var top = parseFloat(window.getComputedStyle(this.navWrap).top);
        if (isNaN(top)) top = 0;
  
        return top + this.navWrap.offsetHeight + 8;
      }
  
      measureNav() {
        if (!this.navWrap) return;
        this.style.setProperty('--ccp-nav-height', this.navWrap.offsetHeight + 'px');
      }
  
      watchNavSize() {
        if (window.ResizeObserver && this.navWrap) {
          this.resizeObserver = new ResizeObserver(() => this.measureNav());
          this.resizeObserver.observe(this.navWrap);
          return;
        }
  
        this.onResizeBound = () => this.measureNav();
        window.addEventListener('resize', this.onResizeBound, { passive: true });
      }
  
      /* ---------------------------------------------------------------- *
       * Navigation
       * ---------------------------------------------------------------- */
  
      bindNav() {
        this.links.forEach((link) => {
          link.addEventListener('click', (event) => {
            var key = link.getAttribute('data-ccp-link');
            if (!this.groupFor(key)) return; /* let the plain anchor do its job */
  
            event.preventDefault();
            this.markEngaged();
  
            /* Tapped before the later pages landed: groups above this one are
               still growing, so scrolling now would land in the wrong place.
               Wait for the merge rather than scroll to a moving target. */
            if (!this.loaded) {
              this.setPending(key, link);
              return;
            }
  
            this.goToGroup(key);
          });
        });
      }
  
      goToGroup(key) {
        var group = this.groupFor(key);
        if (!group || group.hidden) return;
  
        this.suppressSpyUntil = Date.now() + SPY_SUPPRESS_MS;
        this.activeKey = key;
        this.setActive(key);
  
        var target = window.pageYOffset + group.getBoundingClientRect().top - this.getScrollOffset();
  
        window.scrollTo({
          top: Math.max(0, Math.round(target)),
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }
  
      setPending(key, link) {
        this.pendingKey = key;
  
        this.links.forEach((other) => other.classList.remove('is-waiting'));
        if (link) link.classList.add('is-waiting');
  
        if (this.pendingTimer) clearTimeout(this.pendingTimer);
        this.pendingTimer = setTimeout(() => this.resolvePending(), PENDING_TIMEOUT);
      }
  
      resolvePending() {
        if (!this.pendingKey) return;
  
        var key = this.pendingKey;
        this.pendingKey = null;
  
        if (this.pendingTimer) {
          clearTimeout(this.pendingTimer);
          this.pendingTimer = null;
        }
  
        this.links.forEach((link) => link.classList.remove('is-waiting'));
        this.goToGroup(key);
      }
  
      /* A #ccp-group-… link followed from elsewhere lands before the later pages
         merge, so redo the jump once everything is in place. */
      captureIncomingHash() {
        var hash = window.location.hash;
        if (!hash || hash.indexOf('#ccp-group-') !== 0) return;
  
        var target;
        try {
          target = this.querySelector(hash);
        } catch (error) {
          return;
        }
        if (!target) return;
  
        this.markEngaged();
        this.pendingKey = target.getAttribute('data-ccp-group');
      }
  
      setActive(key) {
        var activeLink = null;
  
        this.links.forEach((link) => {
          var isActive = link.getAttribute('data-ccp-link') === key;
          link.classList.toggle('is-active', isActive);
  
          if (isActive) {
            link.setAttribute('aria-current', 'true');
            activeLink = link;
          } else {
            link.removeAttribute('aria-current');
          }
        });
  
        this.tintBar(activeLink);
        if (activeLink) this.revealCircle(activeLink);
      }
  
      /* Washes the sticky bar to a 10% mix of the active colour. The tint is
         precomputed per block in Liquid, so there is nothing to calculate here
         and no dependency on CSS color-mix(). */
      tintBar(activeLink) {
        if (!this.navWrap) return;
  
        var tint = activeLink ? activeLink.getAttribute('data-ccp-tint') : '';
  
        if (tint) {
          this.navWrap.style.setProperty('--ccp-active-bg', tint);
        } else {
          this.navWrap.style.removeProperty('--ccp-active-bg');
        }
      }
  
      /* Keeps the highlighted circle visible when the row scrolls sideways.
         Scrolls the list itself rather than using scrollIntoView, which would
         also move the page. */
      revealCircle(link) {
        var list = this.navList;
        if (!list || list.scrollWidth <= list.clientWidth + 1) return;
  
        var listRect = list.getBoundingClientRect();
        var linkRect = link.getBoundingClientRect();
        var target = list.scrollLeft + (linkRect.left - listRect.left) - listRect.width / 2 + linkRect.width / 2;
  
        list.scrollTo({
          left: Math.max(0, Math.round(target)),
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }
  
      /* ---------------------------------------------------------------- *
       * Scroll-spy
       * ---------------------------------------------------------------- */
  
      /* One scroll listener drives both the stuck state and the spy. The stuck
         state is needed even when the spy is switched off, so it is not gated. */
      bindScroll() {
        this.onScrollBound = () => {
          /* Nothing is highlighted until the shopper actually moves. */
          this.markEngaged();
  
          if (this.rafId) return;
  
          this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            this.updateStuck();
  
            if (Date.now() < this.suppressSpyUntil) return;
            this.updateSpy();
          });
        };
  
        window.addEventListener('scroll', this.onScrollBound, { passive: true });
      }
  
      /* Retires the "tap a colour" hint once it has done its job. */
      markEngaged() {
        this.engaged = true;
        if (this.getAttribute('data-ccp-engaged') !== 'true') {
          this.setAttribute('data-ccp-engaged', 'true');
        }
      }
  
      updateStuck() {
        if (!this.navWrap) return;
  
        var top = parseFloat(window.getComputedStyle(this.navWrap).top);
        if (isNaN(top)) top = 0;
  
        var isStuck = this.navWrap.getBoundingClientRect().top <= top + 1;
        this.navWrap.classList.toggle('is-stuck', isStuck);
      }
  
      updateSpy() {
        if (!this.spyEnabled || !this.engaged) return;
  
        var line = this.getScrollOffset() + 1;
        var active = null;
  
        for (var i = 0; i < this.groups.length; i++) {
          var group = this.groups[i];
          if (group.hidden) continue;
  
          var rect = group.getBoundingClientRect();
  
          /* The group straddling the offset line is the one being looked at. */
          if (rect.top <= line && rect.bottom > line) {
            active = group;
            break;
          }
  
          /* Otherwise remember the last group already scrolled past, which covers
             gaps between groups and the tail of the page. */
          if (rect.top <= line) active = group;
        }
  
        var key = active ? active.getAttribute('data-ccp-group') : null;
        if (key === OTHER_KEY) key = null; /* the catch-all group has no circle */
  
        if (key === this.activeKey) return;
  
        this.activeKey = key;
        this.setActive(key);
      }
  
      /* ---------------------------------------------------------------- *
       * Loading the remaining pages
       * ---------------------------------------------------------------- */
  
      loadRemainingPages() {
        var requests = [];
  
        for (var page = 2; page <= this.totalPages; page++) {
          requests.push(
            this.fetchPage(page, 0)
              .then((html) => ({ page: page, html: html }))
              .catch((error) => {
                console.warn('[custom-color-plp] page ' + page + ' failed to load', error);
                return { page: page, html: null };
              })
          );
        }
  
        Promise.all(requests)
          .then((results) => {
            /* Requested in parallel, applied in order — product sequence within a
               group must match the collection's own order. */
            results.sort((a, b) => a.page - b.page);
            results.forEach((result) => {
              if (result.html) this.applyPage(result.html);
            });
          })
          .catch((error) => {
            console.warn('[custom-color-plp] merge failed', error);
          })
          .then(() => this.finish());
      }
  
      fetchPage(page, attempt) {
        var base = this.getAttribute('data-ccp-url') || window.location.pathname;
        var sectionId = this.getAttribute('data-ccp-section-id');
        var url = base + '?section_id=' + encodeURIComponent(sectionId) + '&page=' + page;
  
        return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((response) => {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .catch((error) => {
            if (attempt < 1) return this.fetchPage(page, attempt + 1);
            throw error;
          });
      }
  
      applyPage(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.querySelectorAll('[data-ccp-group]');
        incoming.forEach((source) => {
          var key = source.getAttribute('data-ccp-group');
          var target = this.groupFor(key);
          if (!target) return;
  
          var sourceGrid = source.querySelector('[data-ccp-grid]');
          var targetGrid = target.querySelector('[data-ccp-grid]');
          if (!sourceGrid || !targetGrid) return;

          /* The collection can change while its pages are loading in parallel.
             Keep the first card when a product appears in more than one page. */
          var productKeys = new Set();
          Array.from(targetGrid.children).forEach((card) => {
            var key = this.productKey(card);
            if (key) productKeys.add(key);
          });

          while (sourceGrid.firstElementChild) {
            var card = sourceGrid.firstElementChild;
            var key = this.productKey(card);

            if (!key || !productKeys.has(key)) {
              targetGrid.appendChild(card); /* adopts into the live document */
              if (key) productKeys.add(key);
            } else {
              card.remove();
            }
          }
        });
  
      }
  
      /* ---------------------------------------------------------------- *
       * Completion
       * ---------------------------------------------------------------- */
  
      finish() {
        this.loaded = true;
        this.setAttribute('data-ccp-loaded', 'true');
  
        /* A configured colour with no products loses its group and its circle. */
        this.groups.forEach((group) => {
          var grid = group.querySelector('[data-ccp-grid]');
          var isEmpty = !grid || grid.childElementCount === 0;
  
          group.hidden = isEmpty;
  
          var link = this.linkFor(group.getAttribute('data-ccp-group'));
          if (!link) return;
  
          var item = link.closest('[data-ccp-nav-item]');
          if (item) item.hidden = isEmpty;
        });
  
        this.measureNav();
        this.resolvePending();
      }
    }
  
    customElements.define('custom-color-plp', CustomColorPlp);
  })();
  
